import React from 'react';
import { HexTile, TileDetails, Character, ActiveGathering } from './HexWorldMap';
import { calculateHexDistance, getRarityColor } from '../utils/hexWorldMapUtils';

interface HexTileModalProps {
  selectedTile: HexTile;
  tileDetails: TileDetails;
  tiles: HexTile[];
  travelPoints: number;
  characters: Character[];
  selectedCharacterIds: number[];
  selectedMonsterSpawn: number | null;
  activeGathering: ActiveGathering[];
  selectedGatherCharacterId: number | null;
  isLoading: boolean;
  onClose: () => void;
  onTravel: (tileId: number) => void;
  onStartBattle: () => void;
  onToggleCharacter: (characterId: number) => void;
  onSelectMonsterSpawn: (id: number) => void;
  onStartGathering: () => void;
  onStopGathering: (gatheringId: number) => void;
  onSelectGatherCharacter: (characterId: number | null) => void;
}

const HexTileModal: React.FC<HexTileModalProps> = ({
  selectedTile, tileDetails, tiles, travelPoints,
  characters, selectedCharacterIds, selectedMonsterSpawn,
  activeGathering, selectedGatherCharacterId, isLoading,
  onClose, onTravel, onStartBattle, onToggleCharacter,
  onSelectMonsterSpawn, onStartGathering, onStopGathering, onSelectGatherCharacter,
}) => {
  return (
    <div className="tile-modal-overlay" onClick={onClose}>
      <div className="tile-modal hex-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-row">
            <h3>
              {selectedTile.townName ? (
                <>🏰 {selectedTile.townName} ({selectedTile.x},{selectedTile.y})</>
              ) : (
                selectedTile.name
              )}
            </h3>
            <span className="hm-close-btn" onClick={onClose} role="button" tabIndex={0}>×</span>
          </div>
          <div className="modal-header-row">
            <div className="modal-header-meta">
              <span className={`zone-badge zone-${selectedTile.zoneLevel}`}>
                Zone {selectedTile.zoneLevel}
              </span>
              {selectedTile.townType && (
                <span className="zone-badge zone-3">
                  {selectedTile.townType}
                </span>
              )}
            </div>
            {!selectedTile.isCurrentLocation && selectedTile.isUnlocked && (() => {
              const currentTile = tiles.find(t => t.isCurrentLocation);
              const tpCost = calculateHexDistance(currentTile || null, selectedTile);
              const hasEnoughTP = travelPoints >= tpCost;
              return (
                <button
                  onClick={() => onTravel(selectedTile.id)}
                  disabled={!hasEnoughTP || isLoading}
                  className="travel-button"
                >
                  {!hasEnoughTP ? `Need ${tpCost - travelPoints} more TP` : `Travel (${tpCost} TP)`}
                </button>
              );
            })()}
          </div>
        </div>

        <div className="modal-content">
          <p className="tile-description">{selectedTile.description}</p>

          <div className="tile-info-grid">
            <div className="info-item">
              <span className="label">Biome:</span>
              <span className="value">{selectedTile.biomeType}</span>
            </div>
            <div className="info-item">
              <span className="label">Recommended Level:</span>
              <span className="value">{selectedTile.requiredLevel}</span>
            </div>
            {!selectedTile.isUnlocked && (
              <div className="info-item" style={{ gridColumn: '1 / -1', padding: '10px', background: 'rgba(244, 67, 54, 0.1)', border: '1px solid #f44336', borderRadius: '4px' }}>
                <span style={{ color: '#f44336', fontWeight: 'bold' }}>🔒 Locked</span>
                <span style={{ display: 'block', fontSize: '0.85em', marginTop: '5px' }}>
                  Win 3 battles on an adjacent tile to unlock
                </span>
              </div>
            )}
            {!selectedTile.bossName && selectedTile.isUnlocked && selectedTile.battlesWon < 3 && (
              <div className="info-item" style={{ gridColumn: '1 / -1', padding: '10px', background: 'rgba(76, 175, 80, 0.1)', border: '1px solid #4caf50', borderRadius: '4px' }}>
                <span style={{ color: '#4caf50', fontWeight: 'bold' }}>⚔️ Battle Progress</span>
                <div style={{ marginTop: '5px' }}>
                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    {[0, 1, 2].map(i => (
                      <div
                        key={i}
                        style={{
                          width: '30px', height: '30px', borderRadius: '4px',
                          background: i < selectedTile.battlesWon ? '#4caf50' : 'rgba(255,255,255,0.1)',
                          border: '1px solid ' + (i < selectedTile.battlesWon ? '#4caf50' : '#444'),
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85em'
                        }}
                      >
                        {i < selectedTile.battlesWon ? '✓' : (i + 1)}
                      </div>
                    ))}
                    <span style={{ fontSize: '0.85em', marginLeft: '10px' }}>
                      {selectedTile.battlesWon}/3 battles won
                    </span>
                  </div>
                  <small style={{ display: 'block', marginTop: '5px', fontSize: '0.8em', color: '#aaa' }}>
                    Win {3 - selectedTile.battlesWon} more battle{3 - selectedTile.battlesWon !== 1 ? 's' : ''} to unlock adjacent tiles
                  </small>
                </div>
              </div>
            )}
            {!selectedTile.bossName && selectedTile.battlesWon >= 3 && (
              <div className="info-item" style={{ gridColumn: '1 / -1', padding: '8px', background: 'rgba(255, 193, 7, 0.1)', border: '1px solid #ffc107', borderRadius: '4px' }}>
                <span style={{ color: '#ffc107', fontSize: '0.9em' }}>
                  ✨ Mastered ({selectedTile.battlesWon} battles won)
                </span>
              </div>
            )}
          </div>

          {/* Resources Section */}
          {tileDetails.resources.length > 0 && (
            <div className="resources-section">
              <h4>📦 Resources</h4>
              <div className="resources-list">
                {tileDetails.resources.map(resource => (
                  <div key={resource.id} className="resource-item">
                    <div className="resource-header">
                      <span className="resource-name">{resource.resourceType}</span>
                      <span className="resource-rarity" style={{ color: getRarityColor(resource.resourceRarity) }}>
                        {resource.rarityName}
                      </span>
                    </div>
                    <div className="resource-stock">
                      <div className="stock-bar">
                        <div
                          className="stock-fill"
                          style={{
                            width: `${(resource.currentStock / resource.maxStock) * 100}%`,
                            background: getRarityColor(resource.resourceRarity)
                          }}
                        />
                      </div>
                      <span className="stock-text">{resource.currentStock}/{resource.maxStock}</span>
                    </div>
                    <small className="respawn-info">Respawns every {resource.respawnTime} min</small>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gathering Section */}
          {selectedTile.isCurrentLocation && (() => {
            const activeHere = activeGathering.find(g => g.tileId === selectedTile.id);
            return selectedTile.allowsGathering || activeHere;
          })() && (
            <div className="gathering-section">
              <h4>⛏️ Gathering: {selectedTile.gatheringType}</h4>
              {(() => {
                const activeHere = activeGathering.find(g => g.tileId === selectedTile.id);
                if (activeHere) {
                  return (
                    <div className="gathering-active-card">
                      <div style={{ marginBottom: '0.5rem' }}>
                        <strong style={{ color: '#3da86e' }}>{activeHere.characterName} is gathering!</strong>
                      </div>
                      <div style={{ fontSize: '0.85em', color: '#7a96aa', marginBottom: '0.75rem' }}>
                        Collections: {activeHere.totalCollections} | Pending: {activeHere.pendingCollections} | Time: {activeHere.elapsedTime}
                      </div>
                      <button
                        onClick={() => onStopGathering(activeHere.id)}
                        disabled={isLoading}
                        className="gathering-stop-btn"
                      >
                        Stop Gathering & Collect Materials
                      </button>
                    </div>
                  );
                }
                const availableCharacters = characters.filter(c =>
                  !activeGathering.some(g => g.characterId === c.id) && c.currentHP > 0
                );
                if (availableCharacters.length === 0) {
                  return (
                    <p style={{ color: '#7a96aa', fontSize: '0.85em' }}>
                      No available characters. All characters are either gathering elsewhere or defeated.
                    </p>
                  );
                }
                return (
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.85em', color: '#7a96aa' }}>
                      Select character to gather:
                    </label>
                    <select
                      value={selectedGatherCharacterId || ''}
                      onChange={(e) => onSelectGatherCharacter(Number(e.target.value) || null)}
                      className="gathering-select"
                    >
                      <option value="">— Select Character —</option>
                      {availableCharacters.map(char => (
                        <option key={char.id} value={char.id}>
                          {char.name} (Lv.{char.level} {char.class})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={onStartGathering}
                      disabled={!selectedGatherCharacterId || isLoading}
                      className={`gathering-start-btn ${selectedGatherCharacterId ? 'ready' : ''}`}
                    >
                      {selectedGatherCharacterId ? '⛏️ Start Gathering' : 'Select a Character'}
                    </button>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Boss Battle Section */}
          {selectedTile.bossName && (
            <div className="monsters-section">
              <h4>👑 Boss Battle</h4>
              <div className="boss-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '2em' }}>👑</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#ff5252', fontWeight: 'bold', fontSize: '1.2em', marginBottom: '4px' }}>
                      {selectedTile.bossName}
                    </div>
                    <div style={{ color: '#aaa', fontSize: '0.9em' }}>Defeat the boss to liberate this town!</div>
                  </div>
                </div>
                <div style={{ padding: '12px', background: 'rgba(0,0,0,0.3)', borderRadius: '6px', border: '1px solid rgba(255,82,82,0.3)' }}>
                  <div style={{ color: '#ffc107', fontSize: '0.9em', marginBottom: '8px', fontWeight: 'bold' }}>
                    ⚠️ Boss Battle Features:
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.85em', color: '#ccc' }}>
                    <li>Larger battle arena (20x15)</li>
                    <li>Boss has enhanced stats (+50% HP, +2 AP)</li>
                    <li>5 enemies total (boss + 4 minions)</li>
                    <li>💎 Double material rewards on victory!</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Monsters Section */}
          {!selectedTile.bossName && tileDetails.monsters.length > 0 && (
            <div className="monsters-section">
              <h4>⚔️ Monsters</h4>
              <div className="monsters-list">
                {tileDetails.monsters.map((monster, idx) => (
                  <div
                    key={`${monster.id}-${idx}`}
                    className={`monster-item ${selectedMonsterSpawn === monster.id ? 'monster-selected' : ''} ${!monster.isAvailable ? 'monster-unavailable' : ''}`}
                    onClick={() => selectedTile.isCurrentLocation && monster.isAvailable && onSelectMonsterSpawn(monster.id)}
                    style={{ cursor: selectedTile.isCurrentLocation && monster.isAvailable ? 'pointer' : 'default' }}
                  >
                    <div className="monster-info">
                      <span className="monster-name">
                        {selectedMonsterSpawn === monster.id && '⚔️ '}
                        <strong>Spawn {monster.spawnSlot}:</strong> {monster.name} (Lv.{monster.level})
                      </span>
                    </div>
                    <div className="monster-details">
                      <span className="monster-type" style={{ color: monster.isAvailable ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>
                        {monster.isAvailable ? '✓ Available' : `Defeated - Respawns in ${monster.minutesUntilRespawn} min`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Character Selection for Battle */}
          {selectedTile.isCurrentLocation && (tileDetails.monsters.length > 0 || selectedTile.bossName) && (
            <div className="battle-setup-section battle-character-section">
              <h4>⚔️ Select Characters for Battle (Max: {tileDetails.tile.maxPlayerUnits})</h4>
              {characters.length === 0 ? (
                <p style={{ color: '#aaa', fontSize: '0.9em' }}>No characters available. Create a character first!</p>
              ) : (
                <div className="character-selection-list">
                  {characters.map(character => (
                    <div
                      key={character.id}
                      className={`char-card ${selectedCharacterIds.includes(character.id) ? 'char-selected' : ''}`}
                      onClick={() => onToggleCharacter(character.id)}
                    >
                      <div>
                        <div className="char-card-name">{character.name}</div>
                        <div className="char-card-sub">
                          Lv.{character.level} {character.class} | HP: {character.currentHP}/{character.maxHP}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {character.currentHP === 0 && (
                          <span style={{ color: '#d94444', fontSize: '11px', fontWeight: 600 }}>Defeated</span>
                        )}
                        {selectedCharacterIds.includes(character.id) && (
                          <div className="char-check">✓</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ marginTop: '10px', fontSize: '0.85em', color: '#aaa' }}>
                {selectedCharacterIds.length} / {tileDetails.tile.maxPlayerUnits} characters selected
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="modal-actions">
            {selectedTile.isCurrentLocation && (tileDetails.monsters.length > 0 || selectedTile.bossName) && (
              <button
                onClick={onStartBattle}
                disabled={selectedCharacterIds.length === 0 || (!selectedTile.bossName && selectedMonsterSpawn === null) || isLoading}
                className="battle-button"
              >
                {selectedCharacterIds.length === 0
                  ? 'Select Characters to Battle'
                  : !selectedTile.bossName && selectedMonsterSpawn === null
                    ? 'Select a Monster Spawn'
                    : selectedTile.bossName
                      ? `👑 Fight Boss — ${selectedCharacterIds.length} ${selectedCharacterIds.length === 1 ? 'Hero' : 'Heroes'}`
                      : `⚔ Enter Battle — ${selectedCharacterIds.length} ${selectedCharacterIds.length === 1 ? 'Hero' : 'Heroes'}`
                }
              </button>
            )}

            {selectedTile.isCurrentLocation && tileDetails.monsters.length === 0 && !selectedTile.bossName && (
              <div className="current-location-notice">📍 You are here - No monsters available</div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default HexTileModal;
